import { NavLink } from 'react-router-dom'
import dayjs from 'dayjs'
import { FilterMatchMode } from 'primereact/api'
import { Calendar } from 'primereact/calendar'
import { ButtonGroup } from 'react-bootstrap'
import { useState } from 'react'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import PageHeader from '../../../layout/page/PageHeader'
import DataTable from '../../../common/data-table/DataTable'
import ImportPaymentsDialog from './ImportPaymentsDialog'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCheckCircle,
    faClock,
    faEdit,
    faMinusCircle,
} from '@fortawesome/free-solid-svg-icons'
import { useToast } from '../../../common/toast/hooks/useToast'

/** Table row shape from payments list API */
type PaymentRow = Record<string, unknown> & {
    id?: number
    code?: string
    payment_for_label?: string
    payment_status_code?: string
    payment_status?: string
}

type DateFilterOptions = {
    value: unknown
    filterCallback: (value: unknown, index: number) => void
    index: number
}

const Payments = () => {
    const { triggerToast } = useToast()
    const [refreshTable, setRefreshTable] = useState(false)
    const [importDialogVisible, setImportDialogVisible] = useState(false)

    const dateTimeBodyTemplate = (rowData: PaymentRow, field: string) => {
        return rowData[field]
            ? dayjs(rowData[field] as string).format('DD-MMM-YYYY @ HH:mm:ss')
            : ''
    }

    const dateFilterTemplate = (options: DateFilterOptions) => {
        return (
            <Calendar
                value={options.value as Date | null | undefined}
                onChange={(e) => options.filterCallback(e.value, options.index)}
                dateFormat="dd/mm/yy"
                placeholder="dd/mm/yyyy"
                mask="99/99/9999"
            />
        )
    }

    const actionsBodyTemplate = (rowData: PaymentRow) => {
        return (
            <ButtonGroup size="sm">
                <DeleteDialog
                    url={'/finance/payments/delete/' + rowData.id}
                    record={rowData}
                    deletedCallback={() => setRefreshTable(true)}
                    message={
                        <p className="mb-3 pb-3 border-bottom">
                            Are you sure you want to delete payment{' '}
                            <strong> {rowData.code}</strong>?
                        </p>
                    }
                />
                <NavLink
                    to={'/finance/payments/view/' + rowData.id}
                    className="btn btn-outline-primary ms-1"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </NavLink>
            </ButtonGroup>
        )
    }

    const statusTemplate = (rowData: PaymentRow) => {
        return (() => {
            switch (rowData.payment_status_code) {
                case 'CO':
                    return (
                        <span className="text-success">
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="me-2"
                            />
                            {rowData.payment_status}
                        </span>
                    )
                case 'PD':
                    return (
                        <span className="text-warning">
                            <FontAwesomeIcon icon={faClock} className="me-2" />
                            {rowData.payment_status}
                        </span>
                    )
                case 'RJ':
                    return (
                        <span className="text-danger">
                            <FontAwesomeIcon
                                icon={faMinusCircle}
                                className="me-2"
                            />
                            {rowData.payment_status}
                        </span>
                    )
            }
        })()
    }

    return (
        <>
            <PageHeader
                icon="credit-card"
                subtitle="View and manage payment transactions"
                gradient="linear-gradient(135deg, #10b981, #059669)"
                header="Payments"
                buttons={[
                    {
                        variant: 'warning',
                        label: 'Import payments',
                        icon: 'file-import',
                        onClick: () => setImportDialogVisible(true),
                    },
                    {
                        variant: 'success',
                        label: 'Create New',
                        icon: 'plus',
                        url: '/finance/payments/new',
                    },
                ]}
            />
            <ImportPaymentsDialog
                visible={importDialogVisible}
                onHide={() => setImportDialogVisible(false)}
                onSuccess={() => setRefreshTable(true)}
            />
            <DataTable
                refreshTable={refreshTable}
                onRefreshed={() => setRefreshTable(false)}
                toolbarConfig={{
                    includeExportBtn: true,
                }}
                config={{
                    dataRoute: '/finance/payments',
                    first: 0,
                    rows: 10,
                    page: 0,
                    sortField: 'created_at',
                    sortOrder: -1,
                    filters: {
                        global: {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        code: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        payment_for_label: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        amount: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        payment_method: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        payment_status: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        updated_by: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        created_at: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        last_modified_at: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                    },
                    filterDisplay: 'menu',
                }}
                columns={[
                    {
                        field: 'code',
                        header: 'Code',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        headerStyle: {
                            maxWidth: '1.8rem',
                        },
                        style: {
                            maxWidth: '1.8rem',
                        },
                    },
                    {
                        field: 'payment_for_label',
                        header: 'What this payment is for',
                        sortable: false,
                        filter: true,
                        filterPlaceholder: 'Search',
                        headerStyle: {
                            maxWidth: '2.5rem',
                        },
                        style: {
                            maxWidth: '2.5rem',
                        },
                    },
                    {
                        field: 'amount',
                        header: 'Amount',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7rem',
                            minWidth: '7rem',
                            maxWidth: '7rem',
                        },
                        style: {
                            width: '7rem',
                            minWidth: '7rem',
                            maxWidth: '7rem',
                        },
                    },
                    {
                        field: 'payment_method',
                        header: 'Method',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '9rem',
                            minWidth: '9rem',
                            maxWidth: '9rem',
                        },
                        style: {
                            width: '9rem',
                            minWidth: '9rem',
                            maxWidth: '9rem',
                        },
                    },
                    {
                        field: 'payment_status',
                        header: 'Status',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        fixedCenter: true,
                        alignHeader: 'center',
                        body: statusTemplate,
                        headerStyle: {
                            width: '10rem',
                            minWidth: '10rem',
                            maxWidth: '10rem',
                        },
                        style: {
                            width: '10rem',
                            minWidth: '10rem',
                            maxWidth: '10rem',
                        },
                    },
                    {
                        field: 'updated_by',
                        header: 'Updated By',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        headerStyle: {
                            maxWidth: '3rem',
                        },
                        style: {
                            maxWidth: '3rem',
                        },
                        isVisible: false,
                    },
                    {
                        field: 'created_at',
                        header: 'Created At',
                        sortable: true,
                        filter: true,
                        dataType: 'date',
                        body: (rowData: PaymentRow) =>
                            dateTimeBodyTemplate(rowData, 'created_at'),
                        filterElement: dateFilterTemplate,
                        style: {
                            maxWidth: '3rem',
                        },
                    },
                    {
                        field: 'last_modified_at',
                        header: 'Last modified',
                        sortable: true,
                        filter: true,
                        dataType: 'date',
                        body: (rowData: PaymentRow) =>
                            dateTimeBodyTemplate(rowData, 'last_modified_at'),
                        filterElement: dateFilterTemplate,
                        style: {
                            maxWidth: '3rem',
                        },
                        isVisible: false,
                    },
                    {
                        field: 'id',
                        header: 'Actions',
                        sortable: false,
                        body: actionsBodyTemplate,
                        alignHeader: 'center',
                        headerStyle: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        style: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        toggleVisibility: false,
                    },
                ]}
                globalFilterFields={[
                    'code',
                    'payment_for_label',
                    'amount',
                    'payment_method',
                    'payment_status',
                ]}
            />
        </>
    )
}

export default Payments
